import React, { useId, useState } from "react";

import { AnimatePresence, m } from "motion/react";

import {
  WidgetBody,
  WidgetChevron,
  WidgetContainer,
  WidgetHeaderRow,
  WidgetTitle,
} from "src/design-system/Widget/parts";

type ControlledProps = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  defaultOpen?: never;
};

type UncontrolledProps = {
  defaultOpen?: boolean;
  onOpenChange?: never;
  isOpen?: never;
};

type CollapsibleWidgetProps = (ControlledProps | UncontrolledProps) & {
  title: React.ReactNode;
  rightSlot?: React.ReactNode;
  children: React.ReactNode;
  dataLoc?: string;
};

// Intentionally not built on Headless UI's Disclosure: the component is simple
// enough to wire ARIA manually, and Disclosure has no controlled `open` API —
// supporting controlled state on top of it would require fighting its internal
// state, which is more complex than the plain button + panel below.
export const CollapsibleWidget: React.FC<CollapsibleWidgetProps> = ({
  title,
  rightSlot,
  children,
  dataLoc = "collapsible-widget",
  ...props
}) => {
  const buttonId = useId();
  const panelId = useId();
  const isControlled = props.isOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(
    () => (!isControlled && props.defaultOpen) ?? false,
  );

  const isOpen = isControlled ? props.isOpen : internalOpen;

  const handleToggle = () => {
    if (isControlled) {
      props.onOpenChange(!props.isOpen);
    } else {
      setInternalOpen((prev) => !prev);
    }
  };

  return (
    <WidgetContainer dataLoc={dataLoc}>
      <WidgetHeaderRow rightSlot={rightSlot}>
        <button
          aria-controls={panelId}
          aria-expanded={isOpen}
          className="flex flex-1 items-center gap-x-2 text-left focus:outline-none focus-visible:outline-none"
          data-loc={`${dataLoc}-trigger`}
          id={buttonId}
          type="button"
          onClick={handleToggle}
        >
          <WidgetChevron isOpen={isOpen} />
          <WidgetTitle>{title}</WidgetTitle>
        </button>
      </WidgetHeaderRow>
      <AnimatePresence initial={false}>
        {isOpen && (
          <m.div
            key="panel"
            animate={{
              height: "auto",
              opacity: 1,
              overflow: "hidden",
              transitionEnd: { overflow: "visible" },
            }}
            aria-labelledby={buttonId}
            exit={{ height: 0, opacity: 0, overflow: "hidden" }}
            id={panelId}
            initial={{ height: 0, opacity: 0, overflow: "hidden" }}
            role="region"
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            <WidgetBody>{children}</WidgetBody>
          </m.div>
        )}
      </AnimatePresence>
    </WidgetContainer>
  );
};
